import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../context/AuthContext";
import { toast } from "sonner";
import {
  Database, Activity, AlertTriangle, CheckCircle2, RefreshCw,
  Wifi, WifiOff, Zap, Clock, Users, Server,
} from "lucide-react";

const REFRESH_MS = 15000;

function fmtUptime(s) {
  if (!s && s !== 0) return "—";
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d) return `${d}d ${h}h ${m}m`;
  if (h) return `${h}h ${m}m`;
  return `${m}m`;
}

function fmtTime(iso) {
  if (!iso) return "—";
  return iso.slice(0, 16).replace("T", " ");
}

export default function SystemStatus() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const refresh = useCallback(async () => {
    setBusy(true);
    setErr("");
    try {
      const r = await axios.get(`${API}/admin/system/status`, { headers: getAuthHeaders() });
      setData(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to load system status");
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, REFRESH_MS);
    return () => clearInterval(id);
  }, [refresh]);

  if (!data && !err) {
    return (
      <div className="h-40 flex items-center justify-center" data-testid="system-status-loading">
        <div className="w-5 h-5 border-2 border-[#7DD3FC] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const db = data?.database || {};
  const ma = data?.metaapi || {};
  const stats = data?.stats || {};
  const errors = data?.recent_errors || [];

  const dbHealthy = !!db.connected;
  const masterStream = !!(ma.master && ma.master.streaming_connected);
  const masterDeployed = ma?.master?.deploy_state === "DEPLOYED" && ma?.master?.connection_state === "CONNECTED";
  const cfHealthy = !!ma?.copyfactory?.strategy_id && !ma?.copyfactory?.last_error;

  return (
    <div className="space-y-6" data-testid="system-status-section">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-bold text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>System Status</h2>
          <p className="text-[11px] text-zinc-500 mt-0.5">
            Live health snapshot · refreshes every 15s · checked {fmtTime(data?.checked_at)}
            {data?.uptime_seconds != null && <> · uptime <span className="font-mono text-zinc-300">{fmtUptime(data.uptime_seconds)}</span></>}
          </p>
        </div>
        <button
          data-testid="system-status-refresh"
          onClick={refresh}
          disabled={busy}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-semibold border border-white/10 text-zinc-300 hover:border-white/30 disabled:opacity-40"
        >
          <RefreshCw size={11} className={busy ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {err && (
        <div className="p-3 rounded-md bg-rose-500/10 border border-rose-500/30 text-[12px] text-rose-300" data-testid="system-status-error">
          <AlertTriangle size={12} className="inline mr-1" /> {err}
        </div>
      )}

      {/* Health pills */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <HealthCard
          testid="hc-db"
          icon={Database}
          label="MongoDB"
          ok={dbHealthy}
          value={dbHealthy ? `${db.ping_ms ?? "?"} ms` : "Offline"}
          sub={db.name || "—"}
        />
        <HealthCard
          testid="hc-metaapi"
          icon={Server}
          label="MetaApi Master"
          ok={masterDeployed}
          warn={ma.master && !masterDeployed && ma.master.deploy_state !== "UNDEPLOYED"}
          value={ma.master ? (ma.master.deploy_state || "—") : "Not configured"}
          sub={ma.master ? (ma.master.connection_state || "—") : "Bind a master MT5 first"}
        />
        <HealthCard
          testid="hc-streaming"
          icon={masterStream ? Wifi : WifiOff}
          label="Streaming"
          ok={masterStream}
          value={masterStream ? "Connected" : "Disconnected"}
          sub={ma.master?.last_event_at ? `Last event ${fmtTime(ma.master.last_event_at)}` : "No events yet"}
        />
        <HealthCard
          testid="hc-copyfactory"
          icon={Zap}
          label="CopyFactory"
          ok={cfHealthy}
          value={ma.copyfactory?.strategy_id ? `Strategy ${ma.copyfactory.strategy_id}` : "Not synced"}
          sub={`${ma.copyfactory?.subscriber_count ?? 0} subscribers`}
        />
      </div>

      {/* App stats */}
      <div className="rounded-xl border border-white/10 bg-[#13131A]/60 p-5" data-testid="system-stats-grid">
        <h4 className="text-xs font-bold text-white mb-4 flex items-center gap-2">
          <Users size={12} className="text-[#7DD3FC]" /> Application Stats
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Stat label="Affiliates" value={stats.total_affiliates} />
          <Stat label="Active CENT" value={stats.active_cent} accent="emerald" />
          <Stat label="Bridge ON" value={stats.bridge_enabled} accent="sky" />
          <Stat label="Pending provision" value={stats.pending_provisions} accent={stats.pending_provisions > 0 ? "amber" : "zinc"} />
          <Stat label="Errored users" value={stats.errored_users} accent={stats.errored_users > 0 ? "rose" : "zinc"} />
        </div>
      </div>

      {/* Database collections */}
      <div className="rounded-xl border border-white/10 bg-[#13131A]/60 p-5" data-testid="db-collections">
        <h4 className="text-xs font-bold text-white mb-3 flex items-center gap-2">
          <Database size={12} className="text-[#7DD3FC]" /> Database Collections
          {db.size_mb != null && (
            <span className="ml-2 text-[10px] font-normal text-zinc-500">
              · data <span className="font-mono text-zinc-300">{db.size_mb} MB</span>
              {db.storage_mb != null && <> · storage <span className="font-mono text-zinc-300">{db.storage_mb} MB</span></>}
            </span>
          )}
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {(db.collections || []).map(c => (
            <div key={c.name} data-testid={`db-coll-${c.name}`} className="px-3 py-2 rounded-md bg-zinc-950/40 border border-white/5 flex items-center justify-between">
              <span className="text-[10px] text-zinc-400 font-mono truncate">{c.name}</span>
              <span className="text-[11px] font-bold text-white font-mono">{c.count.toLocaleString()}</span>
            </div>
          ))}
        </div>
      </div>

      {/* MetaApi probe error (if any) */}
      {ma.master?.probe_error && (
        <div className="p-3 rounded-md bg-amber-500/10 border border-amber-500/30 text-[11px] text-amber-200" data-testid="metaapi-probe-error">
          <AlertTriangle size={11} className="inline mr-1" />
          <span className="font-semibold">MetaApi probe error:</span> {ma.master.probe_error}
        </div>
      )}
      {ma.master?.last_error && (
        <div className="p-3 rounded-md bg-rose-500/10 border border-rose-500/30 text-[11px] text-rose-300" data-testid="metaapi-last-error">
          <AlertTriangle size={11} className="inline mr-1" />
          <span className="font-semibold">Master streaming error:</span> {ma.master.last_error}
        </div>
      )}
      {ma.copyfactory?.last_error && (
        <div className="p-3 rounded-md bg-rose-500/10 border border-rose-500/30 text-[11px] text-rose-300" data-testid="copyfactory-last-error">
          <AlertTriangle size={11} className="inline mr-1" />
          <span className="font-semibold">CopyFactory error:</span> {ma.copyfactory.last_error}
        </div>
      )}

      {/* Recent errors table */}
      <div className="rounded-xl border border-white/10 bg-[#13131A]/60 p-5" data-testid="recent-errors-section">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-xs font-bold text-white flex items-center gap-2">
            <AlertTriangle size={12} className="text-rose-400" /> Recent Errors
          </h4>
          <span className="text-[10px] text-zinc-500" data-testid="recent-errors-count">
            {errors.length} affiliate{errors.length === 1 ? "" : "s"} affected
          </span>
        </div>
        {errors.length === 0 ? (
          <div className="px-3 py-6 text-center" data-testid="recent-errors-empty">
            <CheckCircle2 size={20} className="text-emerald-400 mx-auto mb-1" />
            <p className="text-[12px] text-emerald-200 font-semibold">All clear — no recent errors.</p>
            <p className="text-[10px] text-zinc-500 mt-1">Bridge + CopyFactory are running smoothly.</p>
          </div>
        ) : (
          <div className="rounded-lg border border-white/5 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 border-b border-white/10">
                <tr>
                  <th className="px-3 py-2 text-left">Source</th>
                  <th className="px-3 py-2 text-left">Affiliate</th>
                  <th className="px-3 py-2 text-left">MT5 #</th>
                  <th className="px-3 py-2 text-left">Error</th>
                  <th className="px-3 py-2 text-right">At</th>
                </tr>
              </thead>
              <tbody>
                {errors.map((e, i) => (
                  <tr key={`${e.user_id}-${i}`} data-testid={`recent-error-row-${i}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="px-3 py-2 text-[10px]">
                      <span className={`px-1.5 py-0.5 rounded uppercase font-bold ${e.source === "bridge" ? "bg-sky-500/10 text-sky-300" : "bg-amber-500/10 text-amber-300"}`}>
                        {e.source}
                      </span>
                      {e.fail_count > 1 && (
                        <span className="ml-1.5 text-[9px] text-zinc-500 font-mono">×{e.fail_count}</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-white text-[11px]">{e.name || "—"}</div>
                      <div className="text-[10px] text-zinc-500">{e.email}</div>
                    </td>
                    <td className="px-3 py-2 font-mono text-[10px] text-zinc-300">#{e.mt5_login_id || "—"}</td>
                    <td className="px-3 py-2 text-[10px] text-rose-300 max-w-[420px] break-words leading-relaxed">
                      {e.error}
                    </td>
                    <td className="px-3 py-2 text-right text-[10px] text-zinc-500 font-mono whitespace-nowrap">
                      <Clock size={9} className="inline mr-1" />{fmtTime(e.at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function HealthCard({ icon: Icon, label, ok, warn, value, sub, testid }) {
  const tone = ok
    ? { bg: "bg-emerald-500/5", border: "border-emerald-500/30", text: "text-emerald-300", iconCol: "text-emerald-400" }
    : warn
    ? { bg: "bg-amber-500/5", border: "border-amber-500/30", text: "text-amber-300", iconCol: "text-amber-400" }
    : { bg: "bg-rose-500/5", border: "border-rose-500/30", text: "text-rose-300", iconCol: "text-rose-400" };
  return (
    <div data-testid={testid} className={`rounded-xl border ${tone.border} ${tone.bg} p-4`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-[0.15em] text-zinc-400">{label}</span>
        <Icon size={14} className={tone.iconCol} />
      </div>
      <div className={`text-base font-bold ${tone.text} font-mono`}>{value}</div>
      <div className="text-[10px] text-zinc-500 mt-0.5 truncate">{sub}</div>
    </div>
  );
}

function Stat({ label, value, accent = "zinc" }) {
  const tones = {
    zinc: "text-white",
    emerald: "text-emerald-300",
    sky: "text-sky-300",
    amber: "text-amber-300",
    rose: "text-rose-300",
  };
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-1">{label}</div>
      <div className={`text-2xl font-black font-mono ${tones[accent] || tones.zinc}`}>{value ?? 0}</div>
    </div>
  );
}
